// file: `react/src/components/AddImageOverlay/AddImageOverlay.tsx`

import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { fileTypeFromBuffer } from 'file-type';
import imageCompression from 'browser-image-compression';
import { expressURL, getAuthHeaders } from "../../utils/networkUtils";
import { createClientMessageId, fileUploadSignature } from '../../utils/messageListUtils.ts';
import { Button, Alert, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './AddImageOverlay.scss';

const MAX_SOURCE_BYTES = 20 * 1024 * 1024;
const MAX_COMPRESSED_MB = 1;
const MAX_DIMENSION = 1920;

type OverlayPhase = 'idle' | 'validating' | 'compressing' | 'uploading' | 'success' | 'error';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploadErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const serverError = error.response?.data?.error;
    if (typeof serverError === 'string' && serverError) return serverError;
    if (status === 413) return 'Image is too large for the server (max 10 MB).';
    if (status === 401) return 'You are not logged in. Sign in and try again.';
    if (status === 403) return 'You cannot post images in this conversation.';
    if (status === 400) return 'The image upload was rejected. Check the file and try again.';
    if (status && status >= 500) return 'The server could not save your image. Try again shortly.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Upload failed. Check your connection and try again.';
}

type ReplyPayloadResult = {
  id: string | number;
  sender?: string;
  text: string;
};

const AddImageOverlay = ({
  message,
  isVisible,
  onClose,
  refreshMessages,
  threadId,
  encrypted = false,
  buildReplyPayload,
  prepareCaption,
  preparePhoto,
}) => {
  const [isDropActive, setIsDropActive] = useState(false);
  const [phase, setPhase] = useState<OverlayPhase>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [selectedFileName, setSelectedFileName] = useState('');
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const clientMessageIdRef = useRef<string | null>(null);
  const fileSignatureRef = useRef('');
  const busy = phase === 'validating' || phase === 'compressing' || phase === 'uploading';

  const resetInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const resetOverlayState = useCallback(() => {
    dragCounter.current = 0;
    setIsDropActive(false);
    setPhase('idle');
    setStatusMessage('');
    setErrorMessage('');
    setProgress(0);
    setSelectedFileName('');
    clientMessageIdRef.current = null;
    fileSignatureRef.current = '';
    resetInput();
  }, [resetInput]);

  useEffect(() => {
    if (!isVisible) {
      resetOverlayState();
    }
  }, [isVisible, resetOverlayState]);

  const processFile = useCallback(async (file: File | undefined | null) => {
    if (!file || busy) return;

    setErrorMessage('');
    setStatusMessage('');
    setProgress(0);
    setSelectedFileName(file.name);
    setPhase('validating');

    if (file.size > MAX_SOURCE_BYTES) {
      setPhase('error');
      setErrorMessage(`Image is too large (${formatBytes(file.size)}). Choose a photo under 20 MB.`);
      resetInput();
      return;
    }

    try {
      const fileType = await fileTypeFromBuffer(await file.arrayBuffer());
      if (!fileType || !fileType.mime.startsWith('image/')) {
        setPhase('error');
        setErrorMessage('Please choose a valid image file (JPEG, PNG, WebP, etc.).');
        resetInput();
        return;
      }

      let uploadFile = file;
      const needsCompression = file.size / 1024 / 1024 > MAX_COMPRESSED_MB;

      if (needsCompression) {
        setPhase('compressing');
        setStatusMessage('Compressing photo for upload…');
        try {
          const compressedBlob = await imageCompression(file, {
            maxSizeMB: MAX_COMPRESSED_MB,
            maxWidthOrHeight: MAX_DIMENSION,
            useWebWorker: true,
            onProgress: (value) => setProgress(Math.round(value)),
          });
          uploadFile = new File([compressedBlob], file.name, { type: compressedBlob.type || file.type });
        } catch (_) {
          setPhase('error');
          setErrorMessage('Could not compress this image. Try a different photo.');
          resetInput();
          return;
        }
      }

      setPhase('uploading');
      setStatusMessage(encrypted ? 'Encrypting and uploading photo…' : 'Uploading photo…');
      setProgress(needsCompression ? 100 : 0);

      const signature = fileUploadSignature(file);
      if (!clientMessageIdRef.current || fileSignatureRef.current !== signature) {
        clientMessageIdRef.current = createClientMessageId();
        fileSignatureRef.current = signature;
      }

      let captionText = message;
      if (message.trim() && prepareCaption) {
        captionText = await prepareCaption(message);
      }

      let outgoingFile = uploadFile;
      let mediaEncryption = null;
      if (encrypted) {
        if (!preparePhoto) {
          throw new Error('This conversation cannot encrypt photos yet.');
        }
        const prepared = await preparePhoto(uploadFile);
        if (!prepared?.file || !prepared.mediaEncryption) {
          throw new Error('Could not encrypt this photo.');
        }
        outgoingFile = prepared.file;
        mediaEncryption = prepared.mediaEncryption;
      }

      const formData = new FormData();
      formData.append('file', outgoingFile);
      formData.append('type', 'image');
      formData.append('text', captionText);
      formData.append('threadId', threadId);
      formData.append('clientMessageId', clientMessageIdRef.current);
      if (mediaEncryption) {
        formData.append('mediaEncryption', JSON.stringify(mediaEncryption));
      }
      if (buildReplyPayload) {
        const replyPayload: ReplyPayloadResult | undefined = await buildReplyPayload();
        if (replyPayload) {
          formData.append('replyTo', JSON.stringify({
            id: replyPayload.id,
            sender: replyPayload.sender || '',
            text: replyPayload.text
          }));
        }
      }

      await axios.post(`${expressURL()}/upload`, formData, {
        ...getAuthHeaders(),
        onUploadProgress: (event) => {
          if (!event.total) return;
          setProgress(Math.round((event.loaded / event.total) * 100));
        },
      });

      setPhase('success');
      setStatusMessage('Photo sent.');
      setProgress(100);
      clientMessageIdRef.current = null;
      fileSignatureRef.current = '';
      refreshMessages();
      resetInput();
      window.setTimeout(() => onClose(), 600);
    } catch (error) {
      setPhase('error');
      setErrorMessage(uploadErrorMessage(error));
      resetInput();
    }
  }, [busy, message, threadId, encrypted, buildReplyPayload, prepareCaption, preparePhoto, refreshMessages, onClose, resetInput]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void processFile(event.target.files?.[0]);
  };

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    dragCounter.current++;
    if (dragCounter.current === 1) {
      setIsDropActive(true);
    }
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDropActive(false);
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    dragCounter.current = 0;
    setIsDropActive(false);
    const file = event.dataTransfer.files?.[0];
    void processFile(file);
  }, [processFile]);

  const handleAddImageClick = () => {
    if (!busy && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        onClose();
      }
    };

    if (isVisible) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, onClose, busy]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className='drop-container'>
      <div
        className='drop-trigger'
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Button className='close-button' variant="secondary" onClick={onClose} disabled={busy}>
          <FontAwesomeIcon icon={faTimes} />
        </Button>
        <div className='message'>
          {encrypted && (
            <Alert variant="info" className="encryption-warning">
              <strong>Photos are encrypted on this device</strong> with the same AES-GCM key as
              this conversation’s text. The server stores ciphertext. Captions and quoted replies
              encrypt as they already do.
            </Alert>
          )}
          {message && <p className="caption-preview">“{message}”</p>}
          {selectedFileName && phase !== 'idle' && (
            <p className="selected-file">{selectedFileName}</p>
          )}
          {statusMessage && phase !== 'error' && phase !== 'idle' && (
            <p className="status-message" aria-live="polite">{statusMessage}</p>
          )}
          {errorMessage && (
            <Alert variant="danger" className="upload-error" aria-live="assertive">
              {errorMessage}
            </Alert>
          )}
          {(phase === 'compressing' || phase === 'uploading') && (
            <ProgressBar
              now={progress}
              label={`${progress}%`}
              className="upload-progress"
              animated={phase === 'uploading' && progress < 100}
            />
          )}
          {phase === 'success' && (
            <Alert variant="success" className="upload-success" aria-live="polite">
              {statusMessage}
            </Alert>
          )}
          <Button variant="secondary" onClick={handleAddImageClick} disabled={busy}>
            {busy ? 'Working…' : phase === 'error' ? 'Choose another image' : 'Add image'}
          </Button>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={busy}
        />
        {isDropActive && !busy && (
          <div className="drop-overlay">
            <div className="drop-dialog">
              <p>Drop an image anywhere</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddImageOverlay;
